/**
 * Pull a bottom sheet down to close it.
 *
 * The gesture every sheet on a phone is expected to have, and the reason it is
 * a hook: `ui/dialog.tsx` had it and the vitals pin did not, so two things that
 * look identical to a thumb behaved differently. One implementation, so a sheet
 * cannot be built without it or with a subtly different threshold.
 *
 * ## What the caller supplies
 *
 * `onDismiss` — what a completed pull means. A modal closes; a non-modal panel
 * may simply collapse. The hook does not know or care which.
 *
 * `sheetOf` — how to find the element to move, given the handle the gesture
 * started on. Passed in rather than held as a ref because the dialog finds its
 * sheet by `closest()` (its content may already carry a caller's `ref`, and
 * this way nothing fights over it) while a simpler panel can hand back a ref's
 * current value.
 *
 * ## Why refs, not state
 *
 * A `translateY` per touchmove through React state would re-render the whole
 * subtree at 60 Hz for a purely visual gesture. The transform is written
 * straight to the node and cleared on release.
 *
 * ## Why only the handle drags
 *
 * Putting the gesture on the whole sheet fights every scrollable list inside
 * one — a flick to scroll reads as a flick to dismiss. The handle is a small
 * dedicated target, `touch-none` so the browser does not claim the drag as a
 * scroll, and `aria-hidden` because it is for thumbs: Escape, a close button
 * and the overlay remain the accessible ways out.
 */

import { useRef } from 'react';

/** How far the sheet must be pulled down, in px, before letting go dismisses it. */
export const DRAG_DISMISS_PX = 80;

/** The touch handlers to spread onto a grab handle. */
export interface DragDismissHandlers {
  readonly onTouchStart: (event: React.TouchEvent<HTMLElement>) => void;
  readonly onTouchMove: (event: React.TouchEvent<HTMLElement>) => void;
  readonly onTouchEnd: (event: React.TouchEvent<HTMLElement>) => void;
  readonly onTouchCancel: (event: React.TouchEvent<HTMLElement>) => void;
}

/**
 * @param options.onDismiss - Called when the pull passes {@link DRAG_DISMISS_PX}.
 * @param options.sheetOf - The element to translate, from the handle the touch
 *   began on. Return `null` to make the gesture a no-op.
 */
export function useDragDismiss(options: {
  readonly onDismiss: () => void;
  readonly sheetOf: (handle: HTMLElement) => HTMLElement | null;
}): DragDismissHandlers {
  const drag = useRef<{ readonly startY: number; delta: number } | null>(null);

  /** Put the sheet back where the stylesheet wants it. */
  const release = (handle: HTMLElement): HTMLElement | null => {
    const sheet = options.sheetOf(handle);
    if (sheet === null) return null;
    sheet.style.transition = '';
    sheet.style.transform = '';
    return sheet;
  };

  return {
    onTouchStart: (event) => {
      const touch = event.touches[0];
      if (touch === undefined) return;
      drag.current = { startY: touch.clientY, delta: 0 };
      const sheet = options.sheetOf(event.currentTarget);
      if (sheet !== null) sheet.style.transition = 'none';
    },
    onTouchMove: (event) => {
      const state = drag.current;
      const touch = event.touches[0];
      if (state === null || touch === undefined) return;
      // Only downward: a sheet pulls shut, it does not push taller.
      state.delta = Math.max(0, touch.clientY - state.startY);
      const sheet = options.sheetOf(event.currentTarget);
      if (sheet !== null) sheet.style.transform = `translateY(${state.delta}px)`;
    },
    onTouchEnd: (event) => {
      const state = drag.current;
      drag.current = null;
      const sheet = release(event.currentTarget);
      if (sheet === null || state === null) return;
      // Past the threshold the release dismisses; whatever animates the sheet
      // out carries on from where the thumb left off.
      if (state.delta > DRAG_DISMISS_PX) options.onDismiss();
    },
    onTouchCancel: (event) => {
      drag.current = null;
      release(event.currentTarget);
    },
  };
}
